import type { RefObject } from "react";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { CommunityMember, Post, Space } from "@circle-react/types";
import type { Comment as CommentType } from "@circle-react/types/Comment";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { Comment } from "../Comment";
import { CommentForm } from "../CommentForm";
import { useCommentsList } from "../CommentsList/useCommentsList";

export interface RepliesListProps {
  autofocusReplyForm: boolean;
  communityMemberForReply: CommunityMember | null;
  formRef: RefObject<HTMLFormElement>;
  onCommentCreate: (comment: CommentType) => void;
  onCommentDelete: (comment: CommentType) => void;
  onCommentReplyClick: (params: { communityMember: CommunityMember }) => void;
  parentCommentId: number;
  post: Post;
  preloadedComments: CommentType[];
  preloadedCommentsTotalCount: number;
  resetCommunityMemberForReply: () => void;
  showReplyForm: boolean;
  space: Space;
}

export const RepliesList = ({
  space,
  post,
  parentCommentId,
  preloadedComments = [],
  preloadedCommentsTotalCount = 0,
  showReplyForm = false,
  autofocusReplyForm = false,
  communityMemberForReply,
  onCommentReplyClick,
  onCommentCreate,
  onCommentDelete,
  resetCommunityMemberForReply,
  formRef,
}: RepliesListProps) => {
  const {
    comments,
    hasNextPage,
    fetchNextPage,
    updateCommentInList,
    handleCommentCreate,
    handleCommentDelete,
    formKey,
    persistCacheKey,
    isAnyLoading,
    shouldShowForm,
    isUsedInMinimalTipTapViewModal,
  } = useCommentsList({
    postId: post.id,
    parentCommentId,
    preloaded: true,
    preloadedComments,
    preloadedCommentsTotalCount,
    postCommentsClosed: post.is_comments_closed,
    postLikingDisabled: post.is_liking_disabled,
    space,
    showReplyForm,
    onCommentCreate,
    onCommentDelete,
    resetCommunityMemberForReply,
  });

  return (
    <div className={classNames({ "my-3": !shouldShowForm })}>
      {comments.map(comment => (
        <Comment
          key={comment.id}
          space={space}
          post={post}
          comment={comment}
          autofocusReplyForm
          onReplyClick={onCommentReplyClick}
          onUpdate={updateCommentInList}
          onDelete={handleCommentDelete}
          onReplyCreate={onCommentCreate}
          onReplyDelete={onCommentDelete}
        />
      ))}
      {isAnyLoading && <LoaderRow />}
      {!isAnyLoading && hasNextPage && (
        <div className="load-more mb-4 mt-2 pl-5 text-left text-sm font-medium md:pl-6">
          <button
            type="button"
            onClick={event => {
              event.preventDefault();
              void fetchNextPage();
            }}
            className="focus-visible:outline-secondary load-more__replies text-link focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {t("comments.list.show_more_replies")}
          </button>
        </div>
      )}
      {isAnyLoading && isUsedInMinimalTipTapViewModal && (
        <div className="bg-primary absolute bottom-0 left-0 w-full !px-5 !py-4 md:!px-8">
          <div className="flex gap-4">
            <div className="bg-tertiary size-10 animate-pulse rounded-full" />
            <div className="border-primary h-10 w-full rounded-lg border p-px" />
          </div>
        </div>
      )}
      {shouldShowForm && (
        <CommentForm
          key={formKey}
          spaceId={space.id}
          postId={post.id}
          parentCommentId={parentCommentId}
          communityMemberForReply={communityMemberForReply}
          autoFocus={autofocusReplyForm}
          className={classNames(
            isUsedInMinimalTipTapViewModal && "!max-h-[360px]",
          )}
          onCreate={handleCommentCreate}
          bodyInputRef={formRef}
          persistCacheKey={persistCacheKey}
        />
      )}
    </div>
  );
};
